import moment from 'moment'

import { addSignatureInDD, getBase64FromUrl } from './pdfGenerator/pdfGenerator'
import { DataFile, ReportFile } from '@/@types/global'
import { getFile, getSignatureUser } from '@/api/get'
import { getSignedUrlToPutFile, uploadFileToBucket } from '@/api/put'

export const addOptionAll = (
  array: any,
  key: number = 0,
  label: string = '***'
) => {
  return [
    {
      key,
      value: 'NO_DEF',
      label,
    },
    ...array,
  ]
}

export const getDateNow = () => {
  return {
    pt: moment().format('DD/MM/YYYY'),
    en: moment().format('MM/DD/YYYY'),
  }
}

export const getDateTimeNow = () => {
  return {
    pt: moment().format('DD/MM/YYYY HH:mm:ss'),
    en: moment().format('MM/DD/YYYY HH:mm:ss'),
  }
}

export const downloadBase64 = (
  document: any,
  name: string,
  base64: string,
  byteCharacters: string,
  type:
    | 'application/pdf'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' = 'application/pdf'
) => {
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    byteArrays.push(new Uint8Array(byteNumbers))
  }

  downloadFile(new Blob(byteArrays, { type }), name)
}

export const downloadFile = (blob: any, name: string) => {
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.download = name
  link.href = url
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export const getActionTranslate = (text: any, currentLanguage: string) => {
  let textReturn = text
  if (text === 'Aguardando alteração') {
    textReturn = `Rejeitado - ${textReturn}`
  }
  if (currentLanguage === 'en-US') {
    switch (text) {
      case 'Aguardando revisão':
        textReturn = 'Awaiting review'
        break
      case 'Aguardando aprovação':
        textReturn = 'Awaiting approval'
        break
      case 'Aguardando alteração':
        textReturn = 'Rejected - Awaiting change'
        break
      case 'Aguardando publicação':
        textReturn = 'Awaiting publication'
        break
      case 'Publicado':
        textReturn = 'Published'
        break
      case 'Cancelado':
        textReturn = 'Cancelled'
        break
      default:
        break
    }
  }
  return textReturn
}

export const generateRandomProgress = (
  total: number,
  numCalls: number,
  maxInterval?: number
) => {
  const values = Array.from({ length: numCalls }, () => {
    let randomValue = Math.floor(Math.random() * (total + 1))
    if (maxInterval !== undefined && randomValue > maxInterval) {
      randomValue = maxInterval
    }
    total -= randomValue
    return randomValue
  })
  values[numCalls - 1] += total
  return values
}

export const formatAttachmentName = (name: string) => {
  const nameWithoutExtension = name.split('.')[0]
  const extension = name.split('.').pop()
  return `${nameWithoutExtension}-${Date.now()}.${extension}`
}

export const bytesToMb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2)

export const getFileExtension = (name: string) => name.split('.').pop()

const fonts = {
  Roboto: {
    normal: 'fonts/Roboto-Regular.ttf',
    bold: 'fonts/Roboto-Medium.ttf',
    italics: 'fonts/Roboto-Italic.ttf',
    bolditalics: 'fonts/Roboto-MediumItalic.ttf',
  },
}

export const generatePdf = async (docDefinition: any) => {
  return new Promise((resolve, reject) => {
    try {
      const printer = new PdfPrinter(fonts)
      const doc = printer.createPdfKitDocument(docDefinition)

      const chunks = []

      doc.on('data', (chunk) => {
        chunks.push(chunk)
      })

      doc.on('end', () => {
        const result = Buffer.concat(chunks)
        resolve(result)
      })

      doc.end()
    } catch (err) {
      throw err
    }
  })
}

export const emptyValue = () => {
  return ' - '
}

export const checkEmpty = (value: any) =>
  value !== null &&
  value !== 'null' &&
  value !== undefined &&
  value.toString().trim().length > 0
    ? value
    : emptyValue()

export const dealWithFile = async (
  file: File,
  path: string,
  idReportDocument: number,
  field: string
): Promise<DataFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsArrayBuffer(file)
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const formattedName = formatAttachmentName(file.name)
        const data = {
          id: idReportDocument,
          field,
          extension: getFileExtension(file.name),
          path: `report/${path}`,
          size: `${bytesToMb(file.size)} MB`,
          name: formattedName,
          index: 0,
          url: undefined,
        } as DataFile
        const url = await getSignedUrlToPutFile(data.path) // get presigned url to upload
        await uploadFileToBucket(String(url), e.target.result) // upload file to S3 Bucket

        data.url = url
        resolve(data)
      } catch (error) {
        reject(error)
      }
    }
  })
}

export const getDDWithAllSignature = async (
  document: any,
  dd: any,
  email: string
) => {
  if (document.elaboration_email) {
    const signatureUser = await getSignatureUser({
      email: document.elaboration_email,
    })
    const base64Signature = signatureUser.pathFile
      ? await getBase64FromUrl(signatureUser.pathFile)
      : signatureUser.fileBase64
    await addSignatureInDD(
      dd.content[2].stack,
      document.elaboration_email,
      0,
      signatureUser,
      base64Signature
    )
    if (dd.content[3] && dd.content[3][2]?.stack) {
      await addSignatureInDD(
        dd.content[3][2].stack,
        document.elaboration_email,
        0,
        signatureUser,
        base64Signature
      )
    }
  }
  if (document.test_type === 'Corrosion') {
    if (dd.content[2].stack[8].columns.length === 3) {
      dd.content[2].stack[8].columns.push({ style: 'smallCenter', text: ' ' })
    }

    if (
      document.report_checker_email ||
      document.action === 'Aguardando revisão'
    ) {
      const checkerEmail = document.report_checker_email || email
      const signatureUser = await getSignatureUser({
        email: checkerEmail,
      })
      const base64Signature = signatureUser.pathFile
        ? await getBase64FromUrl(signatureUser.pathFile)
        : signatureUser.fileBase64

      await addSignatureInDD(
        dd.content[2].stack,
        checkerEmail,
        1,
        signatureUser,
        base64Signature
      )
      if (dd.content[3] && dd.content[3][2]?.stack) {
        await addSignatureInDD(
          dd.content[3][2].stack,
          checkerEmail,
          1,
          signatureUser,
          base64Signature
        )
      }
    }
  }
  if (
    document.report_approval_email ||
    document.action === 'Aguardando aprovação'
  ) {
    const approverEmail = document.report_approval_email || email
    const signatureUser = await getSignatureUser({
      email: approverEmail,
    })
    const base64Signature = signatureUser.pathFile
      ? await getBase64FromUrl(signatureUser.pathFile)
      : signatureUser.fileBase64

    await addSignatureInDD(
      dd.content[2].stack,
      approverEmail,
      document.test_type === 'Corrosion' ? 2 : 1,
      signatureUser,
      base64Signature
    )
    if (dd.content[3] && dd.content[3][2]?.stack) {
      await addSignatureInDD(
        dd.content[3][2].stack,
        approverEmail,
        document.test_type === 'Corrosion' ? 2 : 1,
        signatureUser,
        base64Signature
      )
    }
  }

  return dd
}

export const replaceInObject = (
  obj: any,
  searchString: string,
  replaceString: string
) => {
  if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'object') {
        replaceInObject(obj[key], searchString, replaceString)
      } else if (typeof obj[key] === 'string') {
        obj[key] = obj[key].replace(
          new RegExp(searchString, 'g'),
          replaceString
        )
      }
    })
  }
  return obj
}

export const extractFileNameByPath = (path: string) => {
  const regex = /[^\\/]+$/
  const match = path.match(regex)
  return match ? match[0] : ''
}

export const getFilesByPaths = async (reportFile: ReportFile) => {
  if (reportFile.pathPdf) {
    const urlFile = await getFile(reportFile.pathPdf)
    if (!urlFile) {
      throw new Error('File URL not found')
    }

    const responseFile = await fetch(urlFile)
    if (!responseFile.ok) {
      throw new Error(`HTTP: ${responseFile.status}`)
    }
    const blob = await responseFile.blob()
    reportFile.file = blob
  }

  if (reportFile.pathDD) {
    const urlDD = await getFile(reportFile.pathDD)
    if (!urlDD) {
      throw new Error('File URL not found')
    }

    const responseDD = await fetch(urlDD)
    if (!responseDD.ok) {
      throw new Error(`HTTP: ${responseDD.status}`)
    }

    const dd = await responseDD.json()
    reportFile.dd = dd
  }
  return reportFile
}
